import { useMemo } from "react";
import moment from "moment-timezone";

import { Button } from "@/components/ui/button";
import { CommonDestinationTimezone } from "@/lib/utils";

import { GroupCard } from "../components/GroupCard";
import type { BlockingError } from "../state/selectors";
import { getGroupsReadyForUpload } from "../state/selectors";
import type { CustomerMinimal, Group, WizardData } from "../state/types";

type PhaseFinalReviewProps = {
  data: WizardData;
  customers: CustomerMinimal[];
  blocking_errors: BlockingError[];

  onBack: () => void;
  onStartUpload: () => void;
};

function describeBlockingError(error: BlockingError): string {
  switch (error.kind) {
    case "no_period":
      return "Choose a period.";
    case "no_files":
      return "Select at least one PDF.";
    case "suggested_group_unresolved":
      return "A suggested group still needs Approve or Needs-manual-edit.";
    case "ungrouped_files_remain":
      return `${error.count} file${
        error.count === 1 ? " is" : "s are"
      } still ungrouped.`;
    case "group_missing_customer":
      return "A non-empty group has no selected customer.";
    case "duplicate_customer_assignment":
      return `Customer is assigned to ${error.group_ids.length} groups.`;
    case "empty_group_included":
      return "An empty group is present in state.";
    case "file_in_multiple_locations":
      return "A file appears in more than one group.";
    case "file_missing_from_all_locations":
      return "A file is missing from every group and ungrouped.";
    default: {
      const _exhaustive: never = error;
      void _exhaustive;
      return "Unknown blocking error.";
    }
  }
}

/**
 * Phase 4 — Final review.
 *
 * Read-only summary of what's about to be uploaded. Each group renders
 * as a locked `GroupCard` (matching the Phase-3 approved-summary look)
 * so the operator can scroll the full inventory: customer, file count,
 * filenames.
 *
 * The Start Upload button is disabled when any blocking error is
 * present. The operator clicks "Back to manual grouping" to make fixes
 * — the wizard never auto-rolls back to an earlier phase.
 */
export function PhaseFinalReview({
  data,
  customers,
  blocking_errors,
  onBack,
  onStartUpload,
}: PhaseFinalReviewProps) {
  const groupsToUpload = useMemo(
    (): Group[] => getGroupsReadyForUpload(data),
    [data],
  );

  const totalFiles = useMemo((): number => {
    let count = 0;
    for (const g of groupsToUpload) count += g.file_ids.length;
    return count;
  }, [groupsToUpload]);

  const formattedPeriod = useMemo((): string => {
    if (data.period_date === "") return "—";
    const parsed = moment.tz(
      data.period_date,
      "YYYY-MM-DD",
      true,
      CommonDestinationTimezone,
    );
    if (!parsed.isValid()) return data.period_date;
    return parsed.format("MMMM YYYY");
  }, [data.period_date]);

  const canStartUpload = blocking_errors.length === 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold">Final review</h2>
        <p className="text-sm text-muted-foreground">
          Review what will be uploaded. One backend batch will be created
          per customer group below. Nothing has been uploaded yet.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SummaryStat label="Period" value={formattedPeriod} />
        <SummaryStat
          label="Groups"
          value={`${groupsToUpload.length} group${
            groupsToUpload.length === 1 ? "" : "s"
          }`}
        />
        <SummaryStat
          label="Files"
          value={`${totalFiles} PDF${totalFiles === 1 ? "" : "s"}`}
        />
      </div>

      {groupsToUpload.length === 0 ? (
        <div className="rounded border border-dashed border-muted-foreground/40 bg-muted/20 p-6 text-sm text-muted-foreground">
          No groups are ready to upload. Go back to manual grouping to
          finish assigning files.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {groupsToUpload.map((group: Group) => (
            <GroupCard
              key={group.id}
              group={group}
              files={data.files}
              customers={customers}
              showCustomerPicker={false}
              readOnly
            />
          ))}
        </div>
      )}

      {blocking_errors.length > 0 ? (
        <div className="rounded border border-destructive bg-destructive/10 p-2 text-xs text-destructive flex flex-col gap-0.5">
          <span className="font-semibold uppercase tracking-wide text-[10px]">
            Cannot start upload
          </span>
          {blocking_errors.map((e: BlockingError, idx: number) => (
            <span key={`${e.kind}-${idx}`}>• {describeBlockingError(e)}</span>
          ))}
        </div>
      ) : null}

      <div className="flex flex-row justify-end gap-2">
        <Button variant="outline" onClick={onBack}>
          Back to manual grouping
        </Button>
        <Button onClick={onStartUpload} disabled={!canStartUpload}>
          Start upload
        </Button>
      </div>
    </div>
  );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-card p-3 flex flex-col gap-1">
      <span className="text-[10px] uppercase font-semibold tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-sm font-semibold break-words">{value}</span>
    </div>
  );
}
